import { forwardRef, useImperativeHandle, useState, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import IC_MODAL_CLOSE from "@/assets/simulInter/ic_modal_close.svg?react";
import Switch from "@/component/Switch";
import Button from "@/component/Button";
import { generateHotWords, getHotWords, updateHotWords } from "@/services/simulInter";
import { message } from "antd";

const SPLIT_CHAR = "，";

/**
 * 验证单个热词是否符合规则
 * - 不支持特殊字符（只允许中文、英文、数字、空格、连字符）
 * - 纯英文：支持 5 个单词，不限制字数
 * - 中文或中英混合：限制 10 个字符
 */
const validateHotWord = (word: string): { valid: boolean; reason?: string } => {
  const trimmedWord = word.trim();
  if (!trimmedWord) return { valid: true }; // 空词汇（用于输入过程中）

  // 检测是否包含特殊字符（只允许中文、英文、数字、空格、连字符）
  const hasSpecialChars = /[^\u4e00-\u9fa5a-zA-Z0-9\s\-]/.test(trimmedWord);
  if (hasSpecialChars) {
    return { valid: false, reason: '不支持特殊字符，仅支持中文、英文、数字' };
  }

  // 检测是否为纯英文（只包含英文字母、空格、连字符，不包含数字）
  const isPureEnglish = /^[a-zA-Z\s\-]+$/.test(trimmedWord);

  if (isPureEnglish) {
    // 纯英文：检查单词数量（按空格分割）
    const words = trimmedWord.split(/\s+/).filter(w => w);
    if (words.length > 5) {
      return { valid: false, reason: '纯英文热词不能超过 5 个单词' };
    }
    return { valid: true };
  } else {
    // 中文或中英混合：检查字符长度
    if (trimmedWord.length > 10) {
      return { valid: false, reason: '热词不能超过 10 个字符' };
    }
    return { valid: true };
  }
};

interface TransEnhanceModalProps {
  streamName: string;
  speechDraftUrl?: string; // 发言稿 URL，用于重新提取热词
}

export interface TransEnhanceModalRef {
  open: () => void;
  close: () => void;
}

const TransEnhanceModal = forwardRef<
  TransEnhanceModalRef,
  TransEnhanceModalProps
>((props, ref) => {
  const { streamName, speechDraftUrl } = props;
  const [visible, setVisible] = useState(false);
  const [enhance, setEnHance] = useState(false);
  const [hotWords, setHotWords] = useState("");
  const [isRegenerating, setIsRegenerating] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // 标记是否正在进行中文输入法组合输入
  const isComposingRef = useRef(false);

  // 保存初始值，用于判断是否有修改
  const [initialEnhance, setInitialEnhance] = useState(false);
  const [initialHotWords, setInitialHotWords] = useState("");

  const fetchHotWords = useCallback(async () => {
    const res = await getHotWords(streamName);
    const words = res.data.words.join(SPLIT_CHAR);
    setHotWords(words);
    setEnHance(res.data.enhanceEnable);

    // 保存初始值
    setInitialHotWords(words);
    setInitialEnhance(res.data.enhanceEnable);
  }, [streamName]);

  useEffect(() => {
    if (visible) {
      fetchHotWords();
    }
  }, [visible, fetchHotWords]);

  const onClose = async () => {
    try {
      // 过滤掉不符合要求的词汇
      const wordList = hotWords.split(SPLIT_CHAR).filter(word => word.trim());
      const validWords: string[] = [];
      const invalidWords: string[] = [];

      wordList.forEach(word => {
        const validation = validateHotWord(word);
        if (validation.valid) {
          validWords.push(word.trim());
        } else {
          invalidWords.push(word.trim());
        }
      });

      // 如果有不符合要求的词汇被过滤掉，提示用户
      if (invalidWords.length > 0) {
        message.error(`以下热词不符合要求已过滤：${invalidWords.join('、')}`);
      }

      // 使用过滤后的热词
      const filteredHotWords = validWords.join(SPLIT_CHAR);

      // 判断热词是否有修改（使用过滤后的热词与初始值比较）
      const hotWordsChanged = filteredHotWords !== initialHotWords;
      // 判断增强开关是否有修改
      const enhanceChanged = enhance !== initialEnhance;

      // 只有在有修改时才调用接口
      if (hotWordsChanged || enhanceChanged) {
        console.log('有修改，调用 updateHotWords 接口', { hotWordsChanged, enhanceChanged });
        await updateHotWords({
          streamName,
          words: validWords,
          enhanceEnable: enhance,
        });
        message.success('保存成功');
      } else {
        console.log('没有任何修改，不调用接口');
      }

      setVisible(false);

    } catch (error) {
      console.log("onClose error", error);
      setVisible(false);
    }
  };

  const open = () => {
    setVisible(true);
  };

  const onCancel = () => {
    setVisible(false);
    setHotWords("");
    setInitialHotWords("");
  };

  const onRegenerate = async () => {
    if (!speechDraftUrl) {
      message.warning('请先上传发言稿');
      return;
    }

    try {
      setIsRegenerating(true);
      console.log('开始生成热词...', { streamName, url: speechDraftUrl });
      const generateRes = await generateHotWords({ streamName, url: speechDraftUrl });
      const newWords = generateRes.data;

      if (!newWords || newWords.length === 0) {
        message.warning('未生成任何热词');
        return;
      }
      console.log('保存生成的热词...', newWords);
      await updateHotWords({ streamName, words: newWords });

      const wordsStr = newWords.join(SPLIT_CHAR);
      setHotWords(wordsStr);
      setInitialHotWords(wordsStr); // 更新初始值，避免再次保存

      message.success('热词提取成功');
    } catch (error) {
      console.error('重新提取热词失败：', error);
      message.error('热词提取失败，请重试');
    } finally {
      setIsRegenerating(false);
    }
  };

  const onEnhanceChange = (checked: boolean) => {
    setEnHance(checked);
  };

  // 处理热词输入变化
  const handleHotWordsChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // 如果正在进行中文输入法组合输入，需要检查数量限制
    if (isComposingRef.current) {
      const value = e.target.value;
      // 即使在组合输入期间，也要检查分割后的数量是否超过 100
      const totalParts = value.split(SPLIT_CHAR).length;
      if (totalParts > 100) {
        // 超过 100 个，不更新值，保持旧值
        return;
      }
      setHotWords(value);
      return;
    }

    let value = e.target.value;

    // 1. 将英文逗号自动转换为中文逗号
    if (value.includes(',')) {
      value = value.replace(/,/g, SPLIT_CHAR);
    }

    // 2. 过滤特殊字符（实时过滤，保持输入框流畅）
    // 只允许：中文、英文、数字、空格、连字符、中文逗号
    const filteredValue = value.split('').filter(char => {
      return /[\u4e00-\u9fa5a-zA-Z0-9\s\-，]/.test(char);
    }).join('');

    // 如果过滤掉了字符，提示用户（这里已经是最终的过滤结果，不会再触发后续的特殊字符检查）
    let hasFilteredChars = false;
    if (filteredValue !== value) {
      message.error('不支持特殊字符，仅支持中文、英文、数字');
      value = filteredValue;
      hasFilteredChars = true;
    }

    // 3. 检查词汇数量限制
    const previousWordCount = hotWords.split(SPLIT_CHAR).filter(word => word.trim()).length;

    // 如果已有 100 个词，且用户新增了逗号（意味着要添加新词），阻止输入
    if (previousWordCount >= 100) {
      // 检查新值是否包含更多的逗号
      const previousCommaCount = (hotWords.match(new RegExp(SPLIT_CHAR, 'g')) || []).length;
      const currentCommaCount = (value.match(new RegExp(SPLIT_CHAR, 'g')) || []).length;

      if (currentCommaCount > previousCommaCount) {
        return;
      }
    }

    // 严格限制：分割后的总数量（包括空词）不能超过 100
    const totalParts = value.split(SPLIT_CHAR).length;
    if (totalParts > 100) {
      return;
    }

    // 4. 检测是否输入了中文逗号（实时校验触发点）
    // 注意：如果刚刚已经过滤了特殊字符，不要在这里重复提示特殊字符错误
    if (value.includes(SPLIT_CHAR) && !hasFilteredChars) {
      const wordList = value.split(SPLIT_CHAR);
      const validWords: string[] = [];
      const invalidWords: { word: string; reason: string }[] = [];

      wordList.forEach(word => {
        const validation = validateHotWord(word);
        if (validation.valid) {
          validWords.push(word);
        } else {
          if (word.trim()) { // 只记录非空的无效词汇
            // 跳过特殊字符错误（因为已经在上面过滤过了）
            if (!validation.reason?.includes('特殊字符')) {
              invalidWords.push({ word: word.trim(), reason: validation.reason || '格式不符' });
            }
          }
        }
      });

      // 如果有词汇被过滤掉，提示用户并过滤
      if (invalidWords.length > 0) {
        // 重新组合符合要求的词汇
        const newValue = validWords.join(SPLIT_CHAR);
        setHotWords(newValue);

        // 显示具体的错误信息
        const firstInvalid = invalidWords[0];
        message.error(`"${firstInvalid.word}" ${firstInvalid.reason}`);

        // 让输入框失焦
        textareaRef.current?.blur();
        return;
      }
    }

    // 5. 更新值
    setHotWords(value);
  }, [hotWords]);

  // 处理中文输入法组合开始
  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  // 处理中文输入法组合结束
  const handleCompositionEnd = useCallback((e: React.CompositionEvent<HTMLTextAreaElement>) => {
    isComposingRef.current = false;
    // 组合输入结束后，手动触发一次过滤处理
    const syntheticEvent = {
      target: { value: e.currentTarget.value }
    } as React.ChangeEvent<HTMLTextAreaElement>;
    handleHotWordsChange(syntheticEvent);
  }, [handleHotWordsChange]);

  useImperativeHandle(ref, () => ({
    open,
    close: onClose,
  }));

  return visible
    ? ReactDOM.createPortal(
      <div
        className={`fixed z-1 w-full h-full inset-0 flex items-center justify-center z-999 bg-black bg-opacity-50`}
      >
        <div className="bg-white w-148 rounded-4 shadow relative overflow-hidden flex flex-col" style={{ height: '450px' }}>
          {/* 关闭按钮 */}
          <div
            className="absolute right-3 top-3 cursor-pointer z-10"
            onClick={() => {
              setHotWords("");
              setInitialHotWords("");
              onClose();
            }}
          >
            <IC_MODAL_CLOSE className="w-8 h-8" />
          </div>

          {/* 标题 */}
          <div className="h-16 flex items-center pl-6 flex-shrink-0">
            <div className="text-4.5 text-mx-secondary font-600 lh-6">
              转写增强
            </div>
          </div>

          {/* 内容区域 */}
          <div className="px-6 flex-1 overflow-y-auto">
            <div className="flex items-center justify-between mt-2.5 mb-4">
              <div className="text-3.75 text-#292C33 font-500">
                开启转写增强，提升转写准确率
              </div>
              <Switch checked={enhance} onChange={onEnhanceChange} />
            </div>
            <div className="text-#292C3380/50 text-3.5 lh-6 mb-2">
              词与词之间需用中文","隔开，最多可输入 100 个热词。纯英文字母热词支持 5 个单词，包含数字、中文或混合热词限制 10 个字符
            </div>

            {/* 热词输入框 */}
            <div className="w-full h-53.75 flex flex-col px-4 py-2.5 bg-#F5F7FA rounded-1">
              <textarea
                ref={textareaRef}
                value={hotWords}
                onChange={handleHotWordsChange}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                disabled={isRegenerating}
                className={`
                    w-full flex-1 border-none 
                    text-3.5 text-#292C33CC/80 outline-none resize-none lh-5.5 bg-transparent
                    ${isRegenerating ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
              />

              {/* 字数统计 */}
              <div className="text-3.5 text-#292C33CC/80 lh-6.25 h-6.25 flex-self-end">
                {hotWords.length > 0 ? hotWords.split(SPLIT_CHAR).length : 0}/100
              </div>
            </div>
          </div>

          {/* 底部按钮区域 */}
          <div className="flex items-center justify-end px-4 py-3.5 border-t border-#000/10 border-1px border-solid">
            <Button
              onClick={onCancel}
              disabled={isRegenerating}
              className="w-24 h-9 flex items-center justify-center rd-2 text-3.5 text-#000000 mr-3 border-1px border-#000/15 border-solid cursor-pointer"
            >
              取消
            </Button>
            <Button
              type="primary"
              onClick={onRegenerate}
              disabled={isRegenerating}
              className="w-24 h-9 flex items-center justify-center rd-2 text-3.5 bg-mx-primary text-#fff cursor-pointer"
            >
              {isRegenerating ? '提取中...' : '重新提取'}
            </Button>
          </div>
        </div>
      </div>,
      document.body
    )
    : null;
});

export default TransEnhanceModal;
